import {
  JsonFormsRendererRegistryEntry,
  OwnPropsOfControl,
  rankWith,
  uiTypeIs,
} from "@jsonforms/core";
import { useFlags } from "launchdarkly-react-client-sdk";

import { CertificateDeliveryOptionControlRenderer } from "./CertificateDeliveryOptionControl";
import { ArrayModalControlRenderer } from "./controls/array-modal-control/ArrayModalControl";
import { ConsentFormDownloadControlRenderer } from "./controls/consent-form-download-control";
import { CredentialHistoryControlRenderer } from "./controls/credential-history-control";
import { EducationDocumentsControlRenderer } from "./controls/education-documents-control";
import { ReferenceCheckControlRenderer } from "./controls/reference-check-control";
import { EducationHistoryControlRenderer } from "./controls/education-history-control";
import { IdentityDocumentFilesControlRenderer } from "./controls/identity-document-files-control";
import { EmploymentHistoryControlRenderer } from "./controls/employment-history-control";
import {
  EmploymentDocumentsControlRenderer,
  EmploymentDocumentTypeControlRenderer,
} from "./controls/employment-documents-control";
import { EmploymentVerificationControlRenderer } from "./controls/konfir";
import { GapHistoryControlRenderer } from "./controls/gap-history-control";
import { ProofOfIncomeControlRenderer } from "./controls/proof-of-income/ProofOfIncomeControl";
import { ResidencesArrayControlRenderer } from "./controls/residences-array-control/ResidencesArrayControl";
import { ResidencesControlRenderer } from "./controls/residences-control";
import { UploadControlRenderer } from "./controls/upload-control";
import { AcicConsentLayoutRenderer } from "./layouts/AcicConsentLayout";
import { AfpConsentLayoutRenderer } from "./layouts/AfpConsentLayout";
import { AuDlConsentLayoutRenderer } from "./layouts/AuDlConsentLayout";
import { IdentityDetailsLayoutRenderer } from "./layouts/IdentityDetailsLayout";
import { CanadianCriminalLayoutRenderer } from "./layouts/CanadianCriminalLayout";
import { CategorizationLayout } from "./layouts/CategorizationLayout";
import { QuickscreenConsentsLayoutRenderer } from "./layouts/QuickscreenConsentsLayout";
import { QuickscreenRegionalConsentLayoutRenderer } from "./layouts/QuickscreenRegionalConsentLayout";
import { ConsentLayoutRenderer } from "./layouts/ConsentLayout";
import { GeneralAuthorizationLayoutRenderer } from "./layouts/GeneralAuthorizationConsentLayout";
import { IncomeVerificationLayoutRenderer } from "./layouts/IncomeVerificationLayout";
import { OneIdLayoutRenderer } from "./layouts/OneIdLayout";
import { SectionLayoutRenderer } from "./layouts/SectionLayout";
import { UKLegalSexControlRenderer } from "./layouts/UKLegalSex";
import { VehicleRegistrationNumberControlRenderer } from "./layouts/vehicle-registration-number/VehicleRegistrationNumber";
import { DbsConsentControlRenderer } from "./layouts/dbs-consent/DBSConsent";
import { DsConsentControlRenderer } from "./layouts/ds-consent/DSConsent";
import { DbsProfileNumberControlRenderer } from "./layouts/dbs-profile-number/DbsProfileNumber";
import { ResidencesDateRangeControlRenderer } from "./layouts/ResidencesDateRangeControl";
import { CustomQuestionnaireLayoutRenderer } from "./layouts/CustomQuestionnaireLayout";
import { FitnessProprietyLayoutRenderer } from "./layouts/FitnessProprietyLayout";
import { WwccCardDetailsLayoutRenderer } from "./layouts/WwccCardDetailsLayout";

export const QUICKSCREEN_REGIONAL_CONSENT_UI_TYPES = [
  "QuickscreenCanadianCriminalConsentSection",
  "QuickscreenDbsConsentSection",
  "QuickscreenEnhancedDbsConsentSection",
  "QuickscreenDsConsentSection",
  "QuickscreenAfpConsentSection",
  "QuickscreenAcicConsentSection",
  "QuickscreenHeddConsentSection",
  "QuickscreenAuradataConsentSection",
  "QuickscreenMvrConsentSection",
] as const;

const quickscreenRegionalConsentRenderers: JsonFormsRendererRegistryEntry[] =
  QUICKSCREEN_REGIONAL_CONSENT_UI_TYPES.map((uiType) => ({
    tester: rankWith(11, uiTypeIs(uiType)),
    renderer: QuickscreenRegionalConsentLayoutRenderer,
  }));

/**
 * Display-only renderers that are safe for use in Case Management.
 *
 * These renderers do **not** make any Applicant Portal API calls.
 *
 * @description
 * Add a renderer here only if it does not depend on `useApplicantAPI` or any Applicant Portal-specific data, or has an `isSummary` UI.
 * Otherwise, if it does depend on those, add it to `applicantInteractiveRenderers`.
 */
export const displayRenderers: JsonFormsRendererRegistryEntry[] = [
  {
    tester: rankWith(10, uiTypeIs("Categorization")),
    renderer: CategorizationLayout,
  },
  {
    tester: rankWith(10, uiTypeIs("ArrayModalControl")),
    renderer: ArrayModalControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("ResidencesControl")),
    renderer: function Render(props: OwnPropsOfControl) {
      const { applicantPortalNewResidencesControl } = useFlags();

      if (applicantPortalNewResidencesControl) {
        return <ResidencesArrayControlRenderer {...props} />;
      }

      return <ResidencesControlRenderer {...props} />;
    },
  },
  {
    tester: rankWith(10, uiTypeIs("ResidencesDateRangeControl")),
    renderer: ResidencesDateRangeControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("EmploymentHistoryControl")),
    renderer: EmploymentHistoryControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("EmploymentDocumentsControl")),
    renderer: EmploymentDocumentsControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("EmploymentDocumentTypeControl")),
    renderer: EmploymentDocumentTypeControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("EducationHistoryControl")),
    renderer: EducationHistoryControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("GapHistoryControl")),
    renderer: GapHistoryControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("CredentialHistoryControl")),
    renderer: CredentialHistoryControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("ReferenceCheckControl")),
    renderer: ReferenceCheckControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("EducationDocumentsControl")),
    renderer: EducationDocumentsControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("IdentityDocumentFilesControl")),
    renderer: IdentityDocumentFilesControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("ProofOfIncomeControl")),
    renderer: ProofOfIncomeControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("UploadControl")),
    renderer: UploadControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("DbsProfileNumberControl")),
    renderer: DbsProfileNumberControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("UKLegalSexControl")),
    renderer: UKLegalSexControlRenderer,
  },
  {
    // TODO ATT-147: confirm UI type identifier matches what backend
    // generate-schemas emits once UK MOT applicant section lands.
    tester: rankWith(10, uiTypeIs("VehicleRegistrationNumberSection")),
    renderer: VehicleRegistrationNumberControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("AfpConsentSection")),
    renderer: AfpConsentLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("AuDlConsentSection")),
    renderer: AuDlConsentLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("WwccCardDetailsSection")),
    renderer: WwccCardDetailsLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("IdentityDetailsSection")),
    renderer: IdentityDetailsLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("ConsentFormDownloadControl")),
    renderer: ConsentFormDownloadControlRenderer,
  },
];

/**
 * Interactive renderers that rely on Applicant Portal APIs.
 *
 * These renderers use `useApplicantAPI` or other dynamic portal-specific APIs,
 * and are **not safe for use in Case Management**.
 *
 * @description
 * Add a renderer here only if it requires data or calls from the Applicant Portal, or doesn't have an `isSummary` UI. Otherwise, add it to `displayRenderers`.
 */
export const applicantInteractiveRenderers: JsonFormsRendererRegistryEntry[] = [
  {
    tester: rankWith(10, uiTypeIs("Category")),
    renderer: SectionLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("OneIdSection")),
    renderer: OneIdLayoutRenderer,
  },
  {
    tester: rankWith(11, uiTypeIs("ConsentsSection")),
    renderer: ConsentLayoutRenderer,
  },
  {
    tester: rankWith(11, uiTypeIs("CustomQuestionnaireSection")),
    renderer: CustomQuestionnaireLayoutRenderer,
  },
  {
    tester: rankWith(11, uiTypeIs("FitnessProprietySection")),
    renderer: FitnessProprietyLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("GeneralAuthorization")),
    renderer: GeneralAuthorizationLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("IncomeVerificationSection")),
    renderer: IncomeVerificationLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("EmploymentVerificationControl")),
    renderer: EmploymentVerificationControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("CanadianCriminalControl")),
    renderer: CanadianCriminalLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("DbsConsentControl")),
    renderer: DbsConsentControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("DsConsentControl")),
    renderer: DsConsentControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("AcicConsentSection")),
    renderer: AcicConsentLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("CertificateDeliveryOptionControl")),
    renderer: CertificateDeliveryOptionControlRenderer,
  },
  {
    tester: rankWith(11, uiTypeIs("QuickscreenConsentsSection")),
    renderer: QuickscreenConsentsLayoutRenderer,
  },
  ...quickscreenRegionalConsentRenderers,
];

/**
 * jsonforms renderers used in AP and CM
 *
 * @remarks
 * ⚠️ Warning: Only displayRenderers are rendered in CM because they don't depend on any API calls.
 * If your renderer uses `useApplicantAPI` or any AP-specific data, please create a custom renderer for it in /apps/case-management/src/application/renderers.tsx
 *
 */
export const renderers: JsonFormsRendererRegistryEntry[] = [
  ...displayRenderers,
  ...applicantInteractiveRenderers,
];
